import React, { type FunctionComponent } from 'react'
import { Button, useTheme, spacing, Text } from '@retailer-platform/shared-components/src/tds'
import styled from '@emotion/styled'
import { usePartnerId } from '@retailer-platform/dashboard/routing'
import { useHistory } from 'react-router-dom'
import {
  WhitelabelSiteWorkflowState,
  type WhitelabelSiteData,
} from '../../../utils/Onboarding.types.ts'
import { OnboardingSiteSetupChecklistStep } from '../checklist-step/OnboardingSiteSetupChecklistStep.tsx'
import { useDomainMessages } from '../../../../utils/domain/intl.ts'

type OnboardingSiteSetupChecklistProps = {
  sites: WhitelabelSiteData[]
  storeConfigurationName?: string
}

export const OnboardingSiteSetupChecklist: FunctionComponent<OnboardingSiteSetupChecklistProps> = ({
  sites,
  storeConfigurationName,
}: OnboardingSiteSetupChecklistProps) => {
  const theme = useTheme()
  const history = useHistory()
  const partnerId = usePartnerId()

  const CardContainer = styled.div({
    display: 'flex',
    height: '100%',
    minHeight: '100%',
    padding: spacing.s16,
    border: `1px solid ${theme.colors.systemGrayscale20}`,
    backgroundColor: theme.colors.systemGrayscale00,
    color: theme.colors.systemGrayscale80,
  })

  const LeftContainer = styled.div({
    flex: '1.8',
    marginRight: '30px',
  })

  const StepContainer = styled.div({
    flex: '1.2',
    display: 'flex',
    flexDirection: 'column',
  })

  // TODO: refactor
  const webSite = sites.find(site => site.type === 'web') as WhitelabelSiteData
  const iosSite = sites.find(site => site.type === 'ios') as WhitelabelSiteData
  const androidSite = sites.find(site => site.type === 'android') as WhitelabelSiteData

  const isSiteSetupComplete = (site: WhitelabelSiteData): boolean =>
    site?.workflowState === WhitelabelSiteWorkflowState.published

  const buildSiteFormPath = (site: WhitelabelSiteData) =>
    `/partners/${partnerId}/warehouses/${site.warehouseId}/onboarding/site-setup-form/${site.id}`

  const messages = useDomainMessages({
    description: 'onboardingDomain.siteSetupChecklist.description',
    completeInitialSetup: 'onboardingDomain.siteSetupChecklist.completeInitialSetup',
    setupIosApp: 'onboardingDomain.siteSetupChecklist.setupIosApp',
    setupAndroidApp: 'onboardingDomain.siteSetupChecklist.setupAndroidApp',
  })

  // TODO: calculate current step

  return (
    <CardContainer>
      <LeftContainer>
        <Text typography="title">
          {storeConfigurationName
            ? `${storeConfigurationName} is ready for setup`
            : 'Site is ready for setup'}
        </Text>
        <Text typography="bodyRegular" style={{ marginTop: '10px', marginBottom: '20px' }}>
          {messages.description}
        </Text>
        <Button variant={'secondary'} onClick={() => history.push(buildSiteFormPath(webSite))}>
          {messages.completeInitialSetup}
        </Button>
      </LeftContainer>
      <StepContainer>
        {webSite && (
          <OnboardingSiteSetupChecklistStep
            stepIndex={1}
            text={messages.completeInitialSetup}
            complete={isSiteSetupComplete(webSite)}
            href={buildSiteFormPath(webSite)}
            dataTestId="site-setup-step-web"
          />
        )}
        {iosSite && (
          <OnboardingSiteSetupChecklistStep
            stepIndex={2}
            text={messages.setupIosApp}
            complete={isSiteSetupComplete(iosSite)}
            href={buildSiteFormPath(iosSite)}
            dataTestId="site-setup-step-ios"
          />
        )}
        {androidSite && (
          <OnboardingSiteSetupChecklistStep
            stepIndex={3}
            text={messages.setupAndroidApp}
            complete={isSiteSetupComplete(androidSite)}
            href={buildSiteFormPath(androidSite)}
            dataTestId="site-setup-step-android"
          />
        )}
      </StepContainer>
    </CardContainer>
  )
}
